import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { useCallback, useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button, Alert } from '../../components/primitives';
import { FormInput } from '../../components/form';
import { AuthLayout } from '../AuthLayout';
import { ResultCard } from '../../components/ResultCard';
import { CheckCircle2 } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const auth = useAuth();

  interface PasswordResetValues {
    code: string;
    password: string;
    passwordVerify: string;
  }
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const [success, setSuccess] = useState<boolean>(false);

  // Deferred navigation lives in an effect so it is cleanup-safe and survives
  // provider re-renders.
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      navigate('/login?email=' + encodeURIComponent(email));
    }, 2000);
    return () => clearTimeout(timer);
  }, [success, email, navigate]);

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const confirmEmailValidation = Yup.object().shape({
    code: Yup.string().required('Password verification code is required.'),
    password: Yup.string()
      .required('A password is required.')
      .min(8)
      .max(16)
      .matches(
        /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
        // The rule the field states above it, said the same way. It used to
        // read "Must Contain 8 Characters, One Uppercase, One Lowercase, One
        // Number and one special case Character" — Title Case, and describing
        // a rule the regex does not actually enforce.
        'Use 8+ characters with a letter, a number and a symbol.'
      ),
    passwordVerify: Yup.string().oneOf(
      [Yup.ref('password'), null],
      // Not "Passwords must match": the reader has not done anything wrong
      // yet, they are part-way through typing the second one.
      "These don't match yet."
    ),
  });

  const handleOnSubmit = useCallback(
    (
      values: PasswordResetValues,
      actions: FormikHelpers<PasswordResetValues>
    ) => {
      setSubmitError(undefined);
      auth &&
        auth
          .forgotPasswordReset({
            username: email,
            code: values.code,
            password: values.password,
          })
          .then(() => {
            setSuccess(true);
          })
          .catch((err) => {
            actions.setSubmitting(false);
            setSubmitError(err.message);
          });
    },
    [auth, email, navigate]
  );

  const handleOnChange = () => {
    setSubmitError(undefined);
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
        <ResultCard
          tone="success"
          icon={CheckCircle2}
          title="Password reset successful"
          description="You can now sign in with your new password."
          testId="password-reset-complete"
        />
      </div>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      // Who it is for, which is the fact the reader needs — a password reset
      // arrives by email and it is easy to be halfway through one for the
      // wrong account. The old subtitle explained the form back to itself and
      // misspelled "received" doing it.
      subtitle={email ? `For ${email}` : 'Enter the code we emailed you, then choose a new password.'}
      banner={
        submitError && (
          <Alert variant="destructive">
            {submitError}
          </Alert>
        )
      }
    >
      <Formik
        initialValues={{
          code: '',
          password: '',
          passwordVerify: '',
        }}
        onSubmit={handleOnSubmit}
        validationSchema={confirmEmailValidation}
      >
        {(props: FormikProps<PasswordResetValues>) => (
          <Form className="space-y-5" onChange={handleOnChange}>
            {/* The code stays. The design's card has only the two password
                fields because it draws the reset arriving through a link,
                but Cognito's confirmForgotPassword takes the code as an
                argument — without this field the form cannot submit. */}
            <FormInput
              label="Code"
              id="code"
              name="code"
              type="text"
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              label="New password"
              id="password"
              name="password"
              type="password"
              // The rule up front rather than as an error after failing it,
              // and a tick the moment it is met — so the reader knows they are
              // done with it without submitting to find out.
              help={`8+ characters with a letter, number and symbol${
                props.values.password && !props.errors.password ? ' ✓' : ''
              }`}
              required
              disabled={props.isSubmitting}
            />
            <FormInput
              label="Repeat it"
              id="passwordVerify"
              name="passwordVerify"
              type="password"
              required
              disabled={props.isSubmitting}
            />
            <Button type="submit" disabled={props.isSubmitting}>
              {props.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Reset password
            </Button>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}
